import React, { useState, useEffect } from "react";
import {
   Box,
  Stack,
  Typography,
  Paper,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
  Tooltip,
  Divider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";

import dayjs from "dayjs";
import axios from "axios";

import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import EditCalendarRoundedIcon from "@mui/icons-material/EditCalendarRounded";
import WorkRoundedIcon from "@mui/icons-material/WorkRounded";
import { data } from "react-router-dom";

//Cleans up the saved API URL
const normalizeApiBase = (raw) => {
  const s = String(raw ?? "").trim().replace(/\/$/, "");
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
};
// new entry row is added  
const makeEmptyPair = () => ({ timeIn: null, timeOut: null});

export default function FingerprintManually() {
  const API_BASE_URL = normalizeApiBase(localStorage.getItem("saved_sett_api_url"));
  const empId = localStorage.getItem("shared_empid");
  const fullName = localStorage.getItem("saved_sett_fullname") || "";
  
  const [date, setDate]= useState(dayjs());
  //Array of time-entry rows; starts with one empty row.
  const [pairs, setPairs] = useState([makeEmptyPair()]);

  const [comment, setComment]=useState("");
  const [saving, setSaving]= useState(false);
  const [feedback, setFeedback]= useState({ open:false, message: "", severity: "success"});

  // EMPLOYEE MNAUAL ENTRY 
const [manualInfo, setManualInfo] = useState({ used:0, limit:null, usedDates:[] });

  //Appends one more blank row to the pairs array.
  const addPair = ()=> setPairs((p) => [...p, makeEmptyPair()]);
//Removes the row at position idx , (always keeps at least one)
  const removePair = (idx) => {
    setPairs((p) => {
        if (p.length <=1) return p;
        const next = p.slice();
        next.splice(idx,1);
        return next;
    });
  };
// update one row into the row at idx 
 const setPair = (idx, patch) => {
    setPairs((prev) => {
      const next = prev.slice();
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

const monthKey = date.format("YYYY-MM");

const loadManualInfo = async () => {
    if (!empId) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/attendance/manual-entry-status`, {
        params: { empId, month: monthKey },
      });
      setManualInfo({
        used: res.data.used,
        limit: res.data.limit,
        usedDates: res.data.usedDates || [],
        block: res.data.block,
      });
    } catch (err) {
      console.error("Failed to load manual entry status:", err);
    }
  };

  useEffect (() => {
    loadManualInfo();
  }, [monthKey]);
  
 const dayAlreadyUsed = manualInfo.usedDates.includes(date.format("YYYY-MM-DD"));
 const overLimit = manualInfo.limit != null && manualInfo.used >= manualInfo.limit && !dayAlreadyUsed ;
 const limitReached = overLimit && Number(manualInfo.block) === 1;

 
 


  const handleSave = async () => {
    if (!empId) {
      setFeedback({ open: true, message: "Missing employee ID.", severity: "error" });
      return; 
    }

    if (!date) {
        setFeedback({ open: true, message: "Pick a date first.", severity: "warning"});
        return;
    }
    
   //Converts every row's timeIn/timeOut (Day.js objects or null) into "HH:mm" strings, or "" if empty. 
    const validPairs = pairs
    .map((p) => ({
        timeIn: p.timeIn ? dayjs(p.timeIn).format("HH:mm") : "",
        timeOut: p.timeOut ? dayjs(p.timeOut).format("HH:mm") : "",
    }))
    .filter((p) => p.timeIn || p.timeOut);

    if (!validPairs.length) {
        setFeedback({ open:true, message: "Enter at least one time.", severity: "warning"});
        return;
    }
  //Shows the saving spinner and clears any old feedback banner
    setSaving(true);
    setFeedback({ open: false, message:"", severity:"success"});


    try {
        //Sends everything to the backend as JSON
        await axios.post(`${API_BASE_URL}/api/attendance/manual-entry`, {
            empId,
            fullName,
            date: date.format("YYYY-MM-DD"),
            comment: comment.trim(),
            pairs: validPairs,
      }); 

      setFeedback({ open: true, message: "Attendance saved.", severity: "success" });
      setPairs([makeEmptyPair()]);
      setComment("");

      loadManualInfo();

    } catch (err) {
      console.error("Failed to save manual attendance:", err);
      setFeedback({
        open: true,
        message: err?.response?.data?.error || "Failed to save. Check your connection.",
        severity: "error",
      }); 
    } finally {
        setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "flex", justifyContent: "center" }}>
      <Paper
        elevation={10}
        sx={{ width: "100%", maxWidth: 640, p: { xs: 2, sm: 3 }, borderRadius: { xs: 3, sm: 4 } }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <EditCalendarRoundedIcon sx={{ color: "#00796b" }} />
          <Typography variant="h6" fontWeight={800} color="#004d40">
            Manual Attendance Entry
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Forgot to tap your fingerprint or use GPS check-in? Enter the times manually here.
        </Typography>

        {manualInfo.limit != null && (
          <Alert severity={overLimit ? "warning" : "info"} sx={{ mb: 2 }}>
            {limitReached
              ? `Manual entry limit reached (${manualInfo.used}/${manualInfo.limit} days this month).`
              : overLimit
                ? `Manual entry limit reached (${manualInfo.used}/${manualInfo.limit} days this month). You can still save, but your supervisor will be notified.`
                : `Manual entry days used this month: ${manualInfo.used}/${manualInfo.limit}`}
          </Alert>
        )}    

        <LocalizationProvider dateAdapter={AdapterDayjs}>
           <DatePicker
            label="Date"
            value={date}
            onChange={(v) => v && setDate(v)}
            slotProps={{ textField: { size: "small", fullWidth: true, sx: { mb: 2.5 } } }}
          />

          <Stack spacing={1.5}>
            {pairs.map((p, idx) => (
              <Paper
                key={idx}
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  border: "1px solid rgba(0,121,107,0.18)",
                  bgcolor: "rgba(0,121,107,0.03)",
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WorkRoundedIcon fontSize="small" sx={{ color: "#00796b" }} />
                    <Typography fontWeight={800} color="#004d40">
                      Entry #{idx + 1}
                    </Typography>
                  </Stack>
                  <Tooltip title={pairs.length <= 1 ? "" : "Remove"}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={pairs.length <= 1 || saving}
                        onClick={() => removePair(idx)}
                      >
                        <DeleteRoundedIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
                  <TimePicker
                    label="Time In"
                    value={p.timeIn}
                    onChange={(v) => setPair(idx, { timeIn: v })}
                    ampm={false}
                    disabled={saving}
                    slotProps={{
                      textField: {
                        size: "small",
                        InputProps: {
                          startAdornment: (
                            <AccessTimeRoundedIcon fontSize="small" sx={{ opacity: 0.6, mr: 0.5 }} />
                          ),
                        },
                      },
                    }}
                  />
                  <TimePicker
                    label="Time Out"
                    value={p.timeOut}
                    onChange={(v) => setPair(idx, { timeOut: v })}
                    ampm={false}
                    disabled={saving}
                    slotProps={{
                      textField: {
                        size: "small",
                        InputProps: {
                          startAdornment: (
                            <AccessTimeRoundedIcon fontSize="small" sx={{ opacity: 0.6, mr: 0.5 }} />
                          ),
                        },
                      },
                    }}
                  />
                </Box>
              </Paper>
            ))}
          </Stack>
        </LocalizationProvider>

        <Button
          startIcon={<AddCircleRoundedIcon />}
          onClick={addPair}
          disabled={saving}
          sx={{ mt: 1.5, fontWeight: 700, textTransform: "none", color: "#00796b" }}
        >
          Add another time entry
        </Button>

        <Divider sx={{ my: 2 }} />

        <TextField
          label="Comment (optional)"
          fullWidth
          multiline
          rows={2}
          size="small"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="e.g. Forgot to tap fingerprint this morning"
          disabled={saving}
          sx={{ mb: 2 }}
        />

        <Button
          variant="contained"
          fullWidth
          onClick={handleSave}
          disabled={saving || limitReached}
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveRoundedIcon />}
          sx={{
            fontWeight: 900,
            textTransform: "none",
            borderRadius: 2,
            py: 1,
            background: "linear-gradient(90deg,#00796b,#26a69a)",
          }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>

        <Collapse in={feedback.open} timeout={200} unmountOnExit>
          <Alert
            severity={feedback.severity}
            sx={{ mt: 2 }}
            onClose={() => setFeedback((f) => ({ ...f, open: false }))}
          >
            {feedback.message}
          </Alert>
        </Collapse>
      </Paper>
    </Box>
  );


    
}
